/**
 * districtSelection.js
 *
 * Turns a districtLookup (#419) result into the shape the onboarding screen
 * renders and stores. ADO #419.
 *
 * WHY THIS IS NOT INSIDE THE PAGE
 *
 * The onboarding page previously did this inline against the Google Civic
 * `representatives` endpoint. That had three problems, in increasing order of
 * seriousness:
 *
 *   1. It needed VITE_GOOGLE_CIVIC_KEY, so the feature was dead on any
 *      deployment without one - which is every deployment this repo has.
 *   2. It read only `/cd:` divisions, so it returned a congressional district
 *      and nothing else. Most of what a voter is actually electing - their
 *      state senator and representative - was never looked up at all.
 *   3. Whatever it found was saved as a bare district string, which is what
 *      #688 established cannot be joined across states.
 *
 * The Census lookup replaces all three: keyless, returns all three layers, and
 * every district carries the layer-namespaced key that candidates are stored
 * under.
 *
 * Keeping it here rather than in the component means the decisions are under
 * test and the page is left as a rendering concern.
 */

import { deriveDistrictKey, STATE_FIPS, districtKey, LAYERS } from './districtGeoid.js';

/**
 * @typedef {{layer: string, label: string, key: string}} SelectedDistrict
 */

/**
 * Converts a lookup result into an ordered, joinable selection.
 *
 * Order is upper chamber, lower chamber, congressional - the order a voter is
 * most likely to recognise, state first.
 *
 * A chamber with no GEOID is dropped rather than emitted with a null key. A
 * key missing its layer prefix would be a bare GEOID, and a bare GEOID is
 * ambiguous between chambers (VA Senate 13 and VA House 13 are both 51013),
 * so a broken key is worse than an absent one.
 *
 * @param {object|null} districts the `districts` field of a lookupDistricts result
 * @returns {{state: string|null, stateName: string|null, matchedAddress: string|null, districts: SelectedDistrict[]}}
 */
export function toSelection(districts) {
  if (!districts || typeof districts !== 'object') {
    return { state: null, stateName: null, matchedAddress: null, districts: [] };
  }

  const entries = [
    [LAYERS.UPPER, districts.stateSenate],
    [LAYERS.LOWER, districts.stateHouse],
    [LAYERS.CONGRESS, districts.congressional],
  ];

  const selected = [];
  for (const [layer, entry] of entries) {
    const key = districtKey(layer, entry?.geoid ?? null);
    if (!key || !entry?.label) continue;
    selected.push({ layer, label: entry.label, key });
  }

  return {
    state: districts.state ?? null,
    stateName: districts.stateName ?? null,
    matchedAddress: districts.matchedAddress ?? null,
    districts: selected,
  };
}

/**
 * A one-line summary a voter can check against what they already know about
 * where they live. Confirming an address they recognise is the only
 * verification step available to them.
 *
 * @param {ReturnType<toSelection>} selection
 * @returns {string}
 */
export function summarise(selection) {
  if (!selection?.districts?.length) return 'No districts found for that address.';
  const place = selection.stateName || selection.state;
  return [place, ...selection.districts.map((d) => d.label)].join(' · ');
}

/**
 * Where the voter's district keys live between visits.
 *
 * localStorage rather than the database, deliberately and temporarily:
 * `user_preferences` has a single `district` TEXT column holding a label, and
 * adding a column for keys means another migration against tables that have no
 * baseline (ADO #387) while four schema drifts are already open (ADO #697).
 * Storing keys here works for signed-in and anonymous voters alike today and
 * commits to nothing. It does NOT survive a different browser, which is the
 * known cost and the reason this should move once #387 lands.
 */
const KEYS_STORAGE = 'civitas_district_keys';

/**
 * Persists the keys. Every access is guarded: localStorage throws in private
 * windows and where site data is blocked, and losing a district preference
 * must never take the page down with it.
 *
 * @param {string[]} keys
 * @returns {boolean} whether it was actually stored
 */
export function saveDistrictKeys(keys) {
  const list = Array.isArray(keys) ? keys.filter((k) => typeof k === 'string' && k) : [];
  try {
    window.localStorage.setItem(KEYS_STORAGE, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

/**
 * Reads the keys back. Returns [] for anything unreadable or malformed rather
 * than throwing, because an empty key set has a defined meaning downstream:
 * ballotFor treats it as "not onboarded yet" and shows the whole state with a
 * stated caveat, which is also the right behaviour for a voter whose storage
 * cannot be read.
 *
 * @returns {string[]}
 */
export function loadDistrictKeys() {
  try {
    const raw = window.localStorage.getItem(KEYS_STORAGE);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((k) => typeof k === 'string' && k) : [];
  } catch {
    return [];
  }
}


/** Resolve explicit chamber labels; never guess the chamber from a bare number. */
export function manualDistrictSelection(state, label) {
  const code = String(state || '').trim().toUpperCase();
  const text = String(label || '').trim();
  if (!text) return [];
  const congressional = /^([A-Z]{2})-(\d{1,2})$/i.exec(text);
  if (congressional && congressional[1].toUpperCase() !== code) return null;
  const district = congressional ? `Congressional District ${Number(congressional[2])}` : text;
  // At-large congressional districts use Census code 00.
  const result = congressional && Number(congressional[2]) === 0 && STATE_FIPS[code]
    ? { key: `cd:${STATE_FIPS[code]}00`, layer: 'cd' }
    : deriveDistrictKey({ state: code, district });
  return result.key ? [{ key: result.key, layer: result.layer, label: district }] : null;
}

/** Read only keys belonging to the selected state, including legacy saved labels. */
export function selectedDistrictKeys(state, label) {
  const code = String(state || '').trim().toUpperCase();
  const prefix = STATE_FIPS[code];
  const keys = loadDistrictKeys().filter(key => {
    const match = /^(cd:\d{4}|sld[ul]:\d{2}[A-Z0-9]{3})$/.test(key);
    return match && prefix && key.split(':')[1].startsWith(prefix);
  });
  const explicit = manualDistrictSelection(code, label);
  if (String(label || '').trim()) {
    // A matching explicit label preserves a multi-chamber address selection.
    // A different label invalidates the old location, including its other layers.
    if (!explicit?.length) {
      // Census labels such as "14th Suffolk District" cannot be re-derived.
      // Preserve their authoritative keys only for the exact saved location.
      try {
        return window.localStorage.getItem('civitas_state') === code &&
          window.localStorage.getItem('civitas_district') === label ? keys : [];
      } catch { return []; }
    }
    return keys.includes(explicit[0].key) ? keys : explicit.map(d => d.key);
  }
  return keys;
}

export function saveLocation(state, districts = []) {
  const code = String(state || '').toUpperCase();
  if (!STATE_FIPS[code]) throw new Error('Select a valid state.');
  const list = Array.isArray(districts) ? districts : [];
  const label = list[0]?.label || '';
  try {
    window.localStorage.setItem('civitas_state', code);
    window.localStorage.setItem('civitas_district', label);
    if (!saveDistrictKeys(list.map(d => d.key))) throw new Error('Storage unavailable');
  } catch {
    throw new Error('Your browser could not save your location. Enable site storage and try again.');
  }
  return label;
}
